import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Card, Input, InputNumber, Modal, Select, Space, Switch, Typography } from 'antd';

import ConditionBuilder from '../common/ConditionBuilder';
import MeasurementValueInput from '../common/MeasurementValueInput';
import CurrencyValueInput from '../common/CurrencyValueInput';
import NotifyActionFields, {
	KNOWN_NOTIFY_KEYS,
	buildNotifyAction,
	emptyNotifyDraft,
	notifyDraftFromAction,
	useCollectionAudienceOptions,
} from './NotifyActionFields';
import ScheduleFields, { timesOf, timesToSchedule } from './ScheduleFields';
import { InputSelect } from '../Input';
import { buildCardHierarchyOptions } from '../cards/CardVisuals';
import { buildThresholdTokens } from '../../utils/automationTokens';
import { createAutomation, patchAutomation } from '../../services/automations';
import { normalizeId } from '../../utils/ids';
import { normalizeSchedule, unitIsTimed } from '../../utils/automationSchedule';
import { userElementsOf } from '../../utils/userElements';
import { allowedCodes } from '../../utils/currency';
import { isMultiMeasure } from '../../utils/units';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from '../items/QueryRefusalPick';

/**
 * Create/edit one VALUE threshold (`kind: 'threshold'`).
 *
 * Its own editor rather than a mode on the event-rule modal, for the same
 * reason the reminder and the generator have theirs: what a threshold asks is
 * not "what happened" but "did this number cross that line", and the fields
 * that express it (an element, an operator, a bound, a band) have nothing in
 * common with an event rule's checkbox row. A mode flag over two unrelated
 * shapes is a form pretending to be a form.
 *
 * As with every sibling: the API validates the WHOLE rule at write time with
 * field-level messages written for humans, so this editor builds the documented
 * JSON, omits every empty optional, and surfaces a rejection verbatim rather
 * than growing a second, drifting copy of the rules.
 *
 * ⚠ TWO THINGS THE EDITOR DELIBERATELY DOES NOT OFFER. `trigger.events` is
 * fixed at created + updated (a deleted record has no value to have crossed
 * anything, and the API refuses it), and `trigger.elementsChanged` is
 * SERVER-DERIVED from the watched element and its bound — a threshold only has
 * something new to say when one of those moved, and an author who got that
 * filter wrong would get silence, not an error.
 */

const { Text } = Typography;

const OP_OPTIONS = [
	{ value: 'lt', label: 'drops below' },
	{ value: 'lte', label: 'drops to or below' },
	{ value: 'gt', label: 'rises above' },
	{ value: 'gte', label: 'reaches or rises above' },
];

const NUMERIC_TYPES = new Set([ 'number', 'decimal', 'integer' ]);
// A currency element is watchable when it allows exactly ONE currency: there
// is no conversion (docs/plans/currency.md), so a bound on a mixed element
// would be a bound on whatever number happened to be typed. The API refuses
// the rest (400).
const singleCurrencyOf = el => {
	const codes = el?.dataType === 'currency' ? allowedCodes( el.options ) : [];
	return codes.length === 1 ? codes[ 0 ] : null;
};
// A measurement that allows several measures (KPR-1085) has no one reading
// either: the API refuses it, as it refuses a mixed currency.
const isWatchable = el => !! el?.name && (
	NUMERIC_TYPES.has( el.dataType ) || ( el.dataType === 'measurement' && ! isMultiMeasure( el.options || {} ) ) || !! singleCurrencyOf( el )
);
const elementLabelOf = el => el?.label?.singular || el?.name;

const DEFAULT_SWEEP = { every: 1, unit: 'day', time: '07:00', timezone: 'UTC' };

/**
 * Which OTHER elements may serve as the bound: the same comparison family, and
 * for a measurement the same MEASURE. The API refuses the rest at authoring
 * (through the very predicate the KQL `{{element}}` reference uses), so
 * offering an incomparable pair here would only be the editor promising
 * something the save is about to refuse.
 */
function boundCandidates( elements, watched ) {
	if ( ! watched ) return [];
	return ( elements || [] ).filter( el => {
		if ( ! isWatchable( el ) || el.name === watched.name ) return false;
		if ( watched.dataType === 'measurement' ) {
			return el.dataType === 'measurement' && el.options?.measure === watched.options?.measure;
		}
		// Money against money, in the same single currency.
		if ( watched.dataType === 'currency' ) {
			return singleCurrencyOf( el ) === singleCurrencyOf( watched );
		}
		return NUMERIC_TYPES.has( el.dataType );
	} );
}

export default function ThresholdRuleEditor( {
	open,
	collectionId,
	rule,
	collectionCards = [],
	getElementsForCard,
	onClose,
	onSaved,
} ) {

	const { message } = App.useApp();

	const [ name, setName ] = useState( '' );
	const [ triggerCardId, setTriggerCardId ] = useState( '' );
	const [ elementName, setElementName ] = useState( '' );
	const [ op, setOp ] = useState( 'lte' );
	const [ boundKind, setBoundKind ] = useState( 'literal' );
	const [ boundValue, setBoundValue ] = useState( '' );
	const [ boundElement, setBoundElement ] = useState( '' );
	const [ bandOn, setBandOn ] = useState( false );
	const [ bandKind, setBandKind ] = useState( 'absolute' );
	const [ bandValue, setBandValue ] = useState( '' );
	const [ renotify, setRenotify ] = useState( '' );
	const [ sweepOn, setSweepOn ] = useState( false );
	const [ sweep, setSweep ] = useState( DEFAULT_SWEEP );
	const [ kql, setKql ] = useState( '' );
	const [ notify, setNotify ] = useState( emptyNotifyDraft() );
	const [ saving, setSaving ] = useState( false );
	// A cross-item condition has no builder yet (it is authored through the
	// API). Dropping it on save would silently un-do somebody's rule.
	const [ related, setRelated ] = useState( null );

	// Seeding happens on OPEN, never straight off the prop: Cancel has to be
	// able to discard edits.
	useEffect( () => {
		if ( ! open ) return;
		const trigger = rule?.trigger || {};
		const watch = rule?.watch || {};
		const bound = watch.bound || {};
		setName( rule?.name || '' );
		setTriggerCardId( normalizeId( trigger.card_id ) || '' );
		setElementName( watch.element || '' );
		setOp( watch.op || 'lte' );
		setBoundKind( bound.kind === 'element' ? 'element' : 'literal' );
		setBoundValue( bound.kind === 'element' || bound.value === undefined || bound.value === null
			? ''
			: ( typeof bound.value === 'object' ? bound.value : `${ bound.value }` ) );
		setBoundElement( bound.kind === 'element' ? ( bound.element || '' ) : '' );
		setBandOn( !! watch.hysteresis );
		setBandKind( watch.hysteresis?.kind || 'absolute' );
		setBandValue( watch.hysteresis?.value === undefined ? '' : `${ watch.hysteresis.value }` );
		setRenotify( watch.renotifyAfterMinutes ? `${ watch.renotifyAfterMinutes }` : '' );
		setSweepOn( !! watch.sweep );
		setSweep( watch.sweep ? normalizeSchedule( watch.sweep ) : DEFAULT_SWEEP );
		setKql( trigger.condition?.kql || '' );
		setRelated( trigger.condition?.related?.length ? trigger.condition.related : null );
		const action = ( Array.isArray( rule?.actions ) ? rule.actions : [] ).find( a => a?.type === 'notify' );
		setNotify( action ? notifyDraftFromAction( action ) : emptyNotifyDraft() );
	}, [ open, rule ] );

	const cardOptions = useMemo( () => buildCardHierarchyOptions( collectionCards ), [ collectionCards ] );
	const { groups, users } = useCollectionAudienceOptions( collectionId, open );

	const elementsFor = useCallback(
		cardId => ( cardId && getElementsForCard ? ( getElementsForCard( cardId ) || [] ) : [] ),
		[ getElementsForCard ]
	);
	const triggerElements = useMemo( () => elementsFor( triggerCardId ), [ elementsFor, triggerCardId ] );
	const watchable = useMemo( () => triggerElements.filter( isWatchable ), [ triggerElements ] );
	const watched = useMemo(
		() => watchable.find( el => el.name === elementName ) || null,
		[ watchable, elementName ]
	);
	const isMeasurement = watched?.dataType === 'measurement';
	const watchedCurrency = singleCurrencyOf( watched );

	const boundOptions = useMemo(
		() => boundCandidates( triggerElements, watched ).map( el => ( { value: el.name, label: elementLabelOf( el ) } ) ),
		[ triggerElements, watched ]
	);

	const triggerUserElements = useMemo(
		() => userElementsOf( triggerElements ).map( el => ( { value: el.name, label: el.label?.singular || el.name } ) ),
		[ triggerElements ]
	);
	// A threshold fire HAS a trigger record, so the full event vocabulary
	// applies — plus the three tokens that say what crossed.
	const tokens = useMemo(
		() => buildThresholdTokens( triggerElements, getElementsForCard ),
		[ triggerElements, getElementsForCard ]
	);

	const buildWatch = () => {
		const watch = { element: elementName, op };
		watch.bound = boundKind === 'element'
			? { kind: 'element', element: boundElement }
			// A measurement literal round-trips as whatever the input produced
			// ("20 kg", or a {value, unit} object); a numeric one is sent as a
			// NUMBER, because the API's type gate is strict and '5' is not 5.
			// A currency literal goes as the control's { amount, currency }
			// (or text typed by hand); the API also reads a bare major-unit number.
			: { kind: 'literal', value: ( isMeasurement || watchedCurrency ) ? boundValue : Number( boundValue ) };
		if ( bandOn && `${ bandValue }`.trim() ) {
			watch.hysteresis = {
				kind: bandKind,
				value: ( isMeasurement && bandKind === 'absolute' ) ? bandValue : Number( bandValue ),
			};
		}
		const minutes = Number.parseInt( renotify, 10 );
		if ( Number.isFinite( minutes ) && minutes > 0 ) watch.renotifyAfterMinutes = minutes;
		if ( sweepOn ) {
			const cadence = { every: Number( sweep.every ) || 1, unit: sweep.unit, timezone: sweep.timezone };
			if ( unitIsTimed( sweep.unit ) ) Object.assign( cadence, timesToSchedule( timesOf( sweep ) ) );
			if ( sweep.unit === 'week' && sweep.daysOfWeek?.length ) cadence.daysOfWeek = [ ...sweep.daysOfWeek ].sort( ( a, b ) => a - b );
			if ( ( sweep.unit === 'month' || sweep.unit === 'year' ) && sweep.dayOfMonth ) cadence.dayOfMonth = Number( sweep.dayOfMonth );
			watch.sweep = cadence;
		}
		return watch;
	};

	const buildPayload = () => {
		// `events` is fixed and `elementsChanged` is the server's: see the
		// header. A condition that was emptied is sent as an explicit NULL,
		// never left out — this editor holds the whole rule, so "no condition"
		// is something the manager decided.
		const trigger = { type: 'item-event', card_id: triggerCardId, events: [ 'created', 'updated' ] };
		const condition = {};
		if ( kql.trim() ) condition.kql = kql.trim();
		if ( related?.length ) condition.related = related;
		trigger.condition = Object.keys( condition ).length ? condition : null;

		const extras = Object.fromEntries(
			Object.entries( ( Array.isArray( rule?.actions ) ? rule.actions : [] ).find( a => a?.type === 'notify' ) || {} )
				.filter( ( [ k ] ) => ! KNOWN_NOTIFY_KEYS.includes( k ) )
		);
		return {
			kind: 'threshold',
			name: name.trim(),
			trigger,
			watch: buildWatch(),
			actions: [ buildNotifyAction( notify, { extras } ) ],
		};
	};

	// An element a condition could mean several of (400 `ambiguous_element`,
	// element ids step 3): the person picks one, or any of these cards, and
	// the rule is saved again with the pick (`elementChoices`).
	// …answering exactly the condition it was made for: any change forgets it.
	const elementPicks = useElementChoices( JSON.stringify( [ triggerCardId, kql, related ] ) );
	const resetElementPicks = elementPicks.reset;
	// Each opening starts without picks.
	useEffect( () => { if ( open ) resetElementPicks(); }, [ open, resetElementPicks ] );

	const handleSave = async () => {
		setSaving( true );
		try {
			const payload = buildPayload();
			// merge:false — this is a whole-rule editor and the payload is the
			// complete state, so the stored watch is REPLACED. It matters more
			// here than anywhere else: a cleared band or a removed sweep has no
			// null to send under a merging PATCH, and "I turned the sweep off"
			// silently doing nothing is the failure this kind cannot afford.
			if ( rule?._id ) await patchAutomation( collectionId, normalizeId( rule._id ), elementPicks.withChoices( { ...payload, merge: false } ) );
			else await createAutomation( collectionId, elementPicks.withChoices( payload ) );
			elementPicks.reset();
			message.success( rule?._id ? 'Threshold saved.' : 'Threshold created.' );
			onSaved?.();
		}
		catch ( err ) {
			// Verbatim: the API's 400s are field-level and written for humans.
			if ( elementPicks.take( err ) ) return;
			message.error( err?.response?.data?.message || 'Could not save the threshold.' );
		}
		finally {
			setSaving( false );
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			onOk={handleSave}
			okText='Save'
			confirmLoading={saving}
			title={rule?._id ? 'Edit threshold' : 'New threshold'}
			width={760}
			destroyOnClose
		>
			<Space direction='vertical' size={12} style={{ width: '100%' }}>

				<div>
					<label htmlFor='thresholdRuleName' className='d-block'>Name</label>
					<Input
						id='thresholdRuleName'
						size='large'
						value={name}
						placeholder='What this watches, in a few words'
						onChange={e => setName( e.target.value )}
					/>
				</div>

				<InputSelect
					elementName='thresholdTriggerCard'
					elementLabel='Card to watch'
					value={triggerCardId || undefined}
					onChange={( n, value ) => {
						setTriggerCardId( value || '' );
						setElementName( '' );
						setBoundElement( '' );
					}}
					options={cardOptions}
					placeholder="Which card's records carry the value?"
					allowClear={false}
				/>

				{triggerCardId ? (<>
					<Card size='small' title='Tell me when'>
						<Space wrap align='start'>
							<Select
								style={{ minWidth: 200 }}
								value={elementName || undefined}
								aria-label='Element to watch'
								placeholder='Which value?'
								options={watchable.map( el => ( { value: el.name, label: elementLabelOf( el ) } ) )}
								onChange={value => { setElementName( value ); setBoundElement( '' ); setBoundValue( '' ); }}
								notFoundContent='This card has no number or measurement elements.'
							/>
							<Select
								style={{ minWidth: 200 }}
								value={op}
								aria-label='Which way it crosses'
								options={OP_OPTIONS}
								onChange={setOp}
							/>
							<Select
								style={{ minWidth: 180 }}
								value={boundKind}
								aria-label='What to compare against'
								options={[
									{ value: 'literal', label: 'a fixed amount' },
									{ value: 'element', label: 'another value on the record' },
								]}
								onChange={setBoundKind}
							/>
						</Space>

						<div style={{ marginTop: 8 }}>
							{boundKind === 'element' ? (
								<Select
									style={{ minWidth: 260 }}
									value={boundElement || undefined}
									aria-label='Bound element'
									placeholder='Which other value?'
									options={boundOptions}
									onChange={setBoundElement}
									notFoundContent='No other element on this card can be compared to that one.'
								/>
							) : isMeasurement ? (
								<MeasurementValueInput
									// Keyed by element: pointing the rule at another
									// measurement opens a fresh control rather than
									// carrying the previous element's unit across.
									key={`bound:${ elementName }`}
									id='thresholdBound'
									raw={boundValue}
									options={watched?.options}
									name={watched?.name}
									aria-label='Bound'
									onChange={value => setBoundValue( value ?? '' )}
								/>
							) : watchedCurrency ? (
								<CurrencyValueInput
									key={`bound:${ elementName }`}
									id='thresholdBound'
									raw={boundValue}
									options={watched?.options}
									name={watched?.name}
									aria-label='Bound'
									onChange={value => setBoundValue( value ?? '' )}
								/>
							) : (
								<InputNumber
									style={{ width: 200 }}
									value={boundValue === '' ? null : Number( boundValue )}
									aria-label='Bound'
									placeholder='Amount'
									onChange={value => setBoundValue( value === null || value === undefined ? '' : `${ value }` )}
								/>
							)}
						</div>

						{boundKind === 'element' && ! boundOptions.length && elementName ? (
							<Alert
								type='info'
								showIcon
								style={{ marginTop: 8 }}
								message='No comparable element'
								description={isMeasurement
									? 'Only another measurement of the same kind of quantity can be the bound.'
									: ( watchedCurrency
										? `Only another amount in ${ watchedCurrency } alone can be the bound.`
										: 'Only another number on this card can be the bound.' )}
							/>
						) : null}
					</Card>

					<Card size='small' title='How often it may say so'>
						<Space align='center' wrap>
							<Switch
								size='small'
								checked={bandOn}
								onChange={setBandOn}
								aria-label='Use a recovery band'
							/>
							<Text>Only treat it as recovered once it clears the bound by</Text>
							{bandOn ? (<>
								{( isMeasurement && bandKind === 'absolute' ) ? (
									<MeasurementValueInput
										key={`band:${ elementName }`}
										id='thresholdBand'
										raw={bandValue}
										options={watched?.options}
										name={watched?.name}
										aria-label='Recovery band'
										onChange={value => setBandValue( value ?? '' )}
									/>
								) : (
									<InputNumber
										style={{ width: watchedCurrency ? 150 : 120 }}
										value={bandValue === '' ? null : Number( bandValue )}
										// A currency band is in MAJOR units of its one currency.
										suffix={watchedCurrency && bandKind === 'absolute' ? <span className='input-passive-suffix'>{watchedCurrency}</span> : undefined}
										aria-label='Recovery band'
										onChange={value => setBandValue( value === null || value === undefined ? '' : `${ value }` )}
									/>
								)}
								<Select
									style={{ width: 140 }}
									value={bandKind}
									aria-label='Band kind'
									options={[
										{ value: 'absolute', label: 'exactly' },
										{ value: 'percent', label: 'per cent' },
									]}
									onChange={setBandKind}
								/>
							</>) : null}
						</Space>
						<Text type='secondary' className='d-block' style={{ marginTop: 4 }}>
							Without a band, a value sitting right on the bound can recover and
							cross again on every save. It still only says so once per crossing.
						</Text>

						<div style={{ marginTop: 12 }}>
							<label htmlFor='thresholdRenotify' className='d-block'>Say it again after</label>
							<Input
								id='thresholdRenotify'
								style={{ maxWidth: 260 }}
								value={renotify}
								inputMode='numeric'
								placeholder='Never (leave empty)'
								suffix='minutes, while it stays crossed'
								onChange={e => setRenotify( e.target.value.replace( /[^0-9]/g, '' ) )}
							/>
						</div>
					</Card>

					<Card size='small' title='Also check on a schedule'>
						<Space align='center'>
							<Switch size='small' checked={sweepOn} onChange={setSweepOn} aria-label='Check on a schedule' />
							<Text>Look at every record on a clock, not only when one is saved</Text>
						</Space>
						<Text type='secondary' className='d-block' style={{ marginTop: 4 }}>
							Worth turning on when the value moves on its own — a calculated
							field that reads today&apos;s date, or a running total over a window
							that resets at midnight — or when you want a crossing repeated.
						</Text>
						{sweepOn ? (
							<div style={{ marginTop: 12 }}>
								<ScheduleFields
									value={sweep}
									onChange={setSweep}
									idPrefix='thresholdSweep'
									timeLabel='At'
									timezoneHint='The sweep runs in this zone.'
								/>
							</div>
						) : null}
					</Card>

					<div>
						<Text strong className='d-block'>Only for records matching</Text>
						<ConditionBuilder
							value={kql}
							onChange={setKql}
							elements={triggerElements}
							collectionId={collectionId}
						/>
						<Text type='secondary'>Validated on save</Text>
					</div>

					{related ? (
						<Alert
							type='info'
							showIcon
							message='This rule also has an advanced cross-item condition (edited via the API); it will be kept as-is.'
						/>
					) : null}

					<Card size='small' title='Then tell somebody'>
						<NotifyActionFields
							draft={notify}
							onChange={setNotify}
							tokens={tokens}
							groups={groups}
							users={users}
							userElements={triggerUserElements}
							collectionId={collectionId}
						/>
					</Card>
				</>) : null}


				<QueryRefusalPick
					inline
					refusal={elementPicks.refusal}
					disabled={saving}
					onPick={value => { elementPicks.pick( value ); handleSave(); }}
					onRepick={() => { elementPicks.clearRefusal(); handleSave(); }}
				/>
			</Space>
		</Modal>
	);
}
